import React, { ChangeEvent, memo, ReactNode, useCallback } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { map } from 'lodash';
import { Trans } from 'react-i18next';

export type PayerDropdownMuiProps<K extends string, V extends string, Nullable extends boolean> = {
  items: { [K in V]?: ReactNode };
  itemKey: K;
  itemValue: Nullable extends true ? V | null : V;
  nullable?: Nullable;
  onUpdate?: (value: Nullable extends true ? V | null : V, itemKey: K) => void;
  disabled?: boolean;
  fullWidth?: boolean;
};

const nullItem = (
  <MenuItem value="NULL">
    <i>
      <Trans i18nKey="components.dropdown.none" />
    </i>
  </MenuItem>
);

const PayerDropdownMUI = <K extends string, V extends string, Nullable extends boolean = false>({
  items,
  itemKey,
  itemValue,
  nullable,
  onUpdate,
  disabled = false,
  fullWidth = false,
}: PayerDropdownMuiProps<K, V, Nullable>) => {
  const menuItems = map(items, (label, key) => (
    <MenuItem key={key} value={key}>
      {label}
    </MenuItem>
  ));

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = (event.target.value === 'NULL' ? null : event.target.value) as Nullable extends true ? V | null : V;
      onUpdate?.(value, itemKey);
    },
    [onUpdate, itemKey],
  );

  if (!nullable && itemValue === null) {
    throw new Error(`missing value for non-nullable dropdown ${itemKey}`);
  }

  return (
    <TextField
      id={itemKey}
      name={itemKey}
      label={itemKey}
      select
      value={itemValue ?? ''}
      onChange={onChange}
      sx={{ minWidth: 150 }}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      {nullable && nullItem}
      {menuItems}
    </TextField>
  );
};

const memoizedPayerDropdownMUI = memo(PayerDropdownMUI) as typeof PayerDropdownMUI;
export { memoizedPayerDropdownMUI as PayerDropdownMUI };
